import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Briefcase, Bot, Phone, ArrowRight, CheckCircle2, X } from "lucide-react";
import { DS } from "@/lib/ds";

const STEPS = [
  {
    id: "clients",
    icon: Users,
    title: "Importa o aggiungi i tuoi clienti",
    description: "Carica il tuo database esistente via Excel, o aggiungi manualmente i primi contatti.",
    cta: "Aggiungi cliente",
    to: "/clienti",
    color: "#0A192F",
  },
  {
    id: "pipeline",
    icon: Briefcase,
    title: "Configura la pipeline del mese",
    description: "Registra le pratiche PP e CQS in lavorazione per avere subito il forecast provvigionale.",
    cta: "Apri pipeline",
    to: "/pipeline",
    color: "#B7955B",
  },
  {
    id: "coach",
    icon: Bot,
    title: "Attiva il Coach AI",
    description: "Il Coach analizza il portafoglio e ti dice esattamente chi chiamare oggi e perché.",
    cta: "Avvia Coach",
    to: "/coach",
    color: "#16A34A",
  },
  {
    id: "daily",
    icon: Phone,
    title: "Gestisci i richiami quotidiani",
    description: "Ogni mattina apri la vista Daily: trovi i clienti da contattare oggi, ordinati per priorità.",
    cta: "Richiami di oggi",
    to: "/clienti?view=daily",
    color: "#2563EB",
  },
];

export default function OnboardingWelcome({ userName, onDismiss }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("ba_onboarding_dismissed", "1"); } catch {}
    if (onDismiss) onDismiss();
  };

  return (
    <div
      className="mb-7 relative animate-fade-in"
      style={{ background: "linear-gradient(145deg, #0A192F 0%, #112240 60%, #172A45 100%)", borderRadius: 6, overflow: "hidden" }}
    >
      {/* Background geometry */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
          {[...Array(5)].map((_, row) =>
            [...Array(5)].map((_, col) => (
              <circle key={`${row}${col}`} cx={`${65 + col * 6}%`} cy={`${20 + row * 20}%`} r="2" fill="rgba(183,149,91,0.12)" />
            ))
          )}
          <line x1="0" y1="100%" x2="100%" y2="0" stroke="rgba(183,149,91,0.08)" strokeWidth="1" />
        </svg>
      </div>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 z-10 flex items-center justify-center w-7 h-7 transition-colors"
        style={{ color: "rgba(255,255,255,0.4)", borderRadius: "50%" }}
        onMouseEnter={e => e.currentTarget.style.color = "#fff"}
        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.4)"}
        title="Nascondi"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>

      <div className="relative z-10 p-8">
        {/* Header */}
        <div className="mb-7">
          <p className="text-[10px] font-semibold uppercase tracking-[0.26em] mb-2" style={{ color: "var(--gold)" }}>
            Benvenuto in Best Agent
          </p>
          <h2 className="font-serif text-2xl font-light text-white tracking-tight">
            Ciao{userName ? `, ${userName}` : ""}. Da dove vuoi partire?
          </h2>
          <p className="text-[13px] mt-2 leading-relaxed" style={{ color: "rgba(148,163,184,0.85)" }}>
            Completa questi 4 passaggi per essere operativo in meno di 10 minuti.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <Link
                key={step.id}
                to={step.to}
                className="group flex flex-col p-5 transition-all duration-200"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 4,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                  e.currentTarget.style.borderColor = "rgba(183,149,91,0.40)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${step.color}25`, borderRadius: 3 }}>
                    <Icon className="w-4 h-4" strokeWidth={1.6} style={{ color: step.color === "#0A192F" ? "#94A3B8" : step.color }} />
                  </div>
                  <span className="text-[10px] font-bold text-white/30 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="text-[13px] font-semibold text-white mb-2 leading-snug">{step.title}</h3>
                <p className="text-[12px] leading-relaxed flex-1 mb-4" style={{ color: "rgba(148,163,184,0.75)" }}>
                  {step.description}
                </p>
                <div className="flex items-center gap-1 text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--gold)" }}>
                  {step.cta} <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom dismiss */}
        <div className="mt-5 flex items-center justify-between">
          <p className="text-[11px]" style={{ color: "rgba(148,163,184,0.5)" }}>
            Questo pannello scompare automaticamente dopo il primo utilizzo.
          </p>
          <button
            onClick={handleDismiss}
            className="text-[11px] font-medium transition-colors"
            style={{ color: "rgba(148,163,184,0.6)" }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(148,163,184,0.6)"}
          >
            Non mostrare più
          </button>
        </div>
      </div>
    </div>
  );
}
